import {
    createEntityAdapter,
    createSlice,
    PayloadAction,
} from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { WorkShiftTimeSlotItems } from "../models/workShitTimeSlot/workShitTimeSlotItems";
import { WorkShiftTimeSlotItem } from "../models/workShitTimeSlot/workShiftTimeSlotItem";
import { Base } from "../framework/base";
import {
    getWorkShiftTimeSlotItems,
    getWorkTimeTypes,
    removeWorkTime,
    getSalaryRowTypes
} from "../services/workTimeBetaService";
import { WorkTimeType } from "../models/workShitTimeSlot/workTimeType";
import { ISalaryRowType } from "../models/workShitTimeSlot/salaryRowType";
import { RootState } from "../framework/customStore";

interface WorkShiftTimeSlotState {
    editId: string;
    selectedId: string;
    workTimeTypes: WorkTimeType[];
    salaryRowTypes: ISalaryRowType[];
}

const initialState: WorkShiftTimeSlotState = {
    editId: null,
    selectedId: null,
    workTimeTypes: null,
    salaryRowTypes: null,
};

const sortComparer = (a, b) =>
    Base.dayjsCompare(a.startDate, b.startDate) ||
    Base.dayjsCompare(a.endDate, b.endDate);

const workShiftTimeSlotAdapter = createEntityAdapter<WorkShiftTimeSlotItem>({
    sortComparer,
});

export const workShiftTimeSlotSlice = createSlice({
    name: "workShiftTimeSlot",
    initialState: workShiftTimeSlotAdapter.getInitialState(initialState),
    reducers: {
        setWorkShiftTimeSlots: (
            state,
            action: PayloadAction<WorkShiftTimeSlotItems>
        ) => {
            // setAll instead of upsertMany to avoid clogging the browser with a lot of slots.
            workShiftTimeSlotAdapter.setAll(state, action.payload.items);
        },
        workShiftTimeSlotReceived: (
            state,
            action: PayloadAction<WorkShiftTimeSlotItem>
        ) => {
            if (action.payload)
                workShiftTimeSlotAdapter.upsertOne(state, action.payload);
        },
        removeWorkShiftTimeSlot: (state, action: PayloadAction<string>) => {
            workShiftTimeSlotAdapter.removeOne(state, action.payload);
        },
        setEditId: (state, action: PayloadAction<string>) => {
            state.editId = action.payload;
        },
        toggleSelectedId: (state, action: PayloadAction<string>) => {
            state.selectedId =
                action.payload === state.selectedId ? null : action.payload;
        },
        setWorkTimeTypes: (state, action: PayloadAction<WorkTimeType[]>) => {
            state.workTimeTypes = action.payload;
        },
        setSalaryRowTypes: (
            state,
            action: PayloadAction<ISalaryRowType[]>
        ) => {
            state.salaryRowTypes = action.payload;
        },
    },
});

export const {
    setWorkShiftTimeSlots,
    workShiftTimeSlotReceived,
    removeWorkShiftTimeSlot,
    setEditId,
    toggleSelectedId,
    setWorkTimeTypes,
    setSalaryRowTypes,
} = workShiftTimeSlotSlice.actions;

export const fetchWorkShiftTimeSlotItems = createAsyncThunk(
    "workShiftTimeSlot/list",
    async(
        {
            startDate,
            endDate,
            employeeIds,
        }: {
            startDate: string;
            endDate: string;
            employeeIds: string | string[];
        },
        thunkApi
    ) => {
        const workShiftTimeSlotItems = await getWorkShiftTimeSlotItems(
            startDate,
            endDate,
            employeeIds,
            thunkApi.signal
        );
        thunkApi.dispatch(setWorkShiftTimeSlots(workShiftTimeSlotItems));
    }
);

export const deleteWorkShiftTimeSlot = createAsyncThunk(
    "workShiftTimeSlot/delete",
    async (
        {
            id,
            allowShiftOverwrite,
        }: { id: string; allowShiftOverwrite: boolean },
        thunkApi
    ) => {
        await removeWorkTime(id, allowShiftOverwrite);
        thunkApi.dispatch(removeWorkShiftTimeSlot(id));
    }
);

export const fetchWorkTimeTypes = createAsyncThunk(
    "workShiftTimeSlot/workTimeTypes",
    async(_, thunkApi) => {
        const workTimeTypes = await getWorkTimeTypes(thunkApi.signal);
        thunkApi.dispatch(setWorkTimeTypes(workTimeTypes));
    }
);

export const fetchSalaryRowTypes = createAsyncThunk(
    "workShiftTimeSlot/salaryRowTypes",
    async(_, thunkApi) => {
        const salaryRowTypes = await getSalaryRowTypes();
        thunkApi.dispatch(setSalaryRowTypes(salaryRowTypes));
    }
);

export const workShiftTimeSlotReducer = workShiftTimeSlotSlice.reducer;

export const workShiftTimeSlotSelector =
    workShiftTimeSlotAdapter.getSelectors();


export const selectSalaryRowTypes = (state: RootState): ISalaryRowType[] => state.workShiftTimeSlot.salaryRowTypes;
